import React, { LegacyRef, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useEditor, EditorContent, Node, EditorEvents, mergeAttributes } from '@tiptap/react';

import { Nullable } from 'src/types/nullable.type';

import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Heading from '@tiptap/extension-heading';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';

import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import BulletList from '@tiptap/extension-bullet-list';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Placeholder from '@tiptap/extension-placeholder';

import TextEditorImage from './extensions/image';
import Iframe from './extensions/iframe';
import DraggableItem from './extensions/draggable-item';

import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';

import Focus from '@tiptap/extension-focus';
import History from '@tiptap/extension-history';
import Dropcursor from '@tiptap/extension-dropcursor';

import HorizontalRule from '@tiptap/extension-horizontal-rule';
import HardBreak from '@tiptap/extension-hard-break';

import MutualActionPlanExtension from './extensions/mutual-action-plan';
import MutualActionPlanActionItemExtension from './extensions/mutual-action-plan-action-item';
import GapCursorFocus from './extensions/gap-cursor-focus';

import { NeueTextEditorBubbleMenu } from './neue-bubble-menu';

import fastDeepEqual from 'fast-deep-equal';
import { NeueTextEditorBubbleMenuFixed } from './neue-bubble-menu-fixed';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom';
import classNames from 'classnames';
import { Size } from 'src/editor/content-creation/types';
import { useMeasure } from 'react-use';
import { useSpotlight } from 'src/common/spotlight/use-spotlight.hook';
import { debounce } from 'lodash';

const TEXT_BLOCK_PADDING = 16;

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    documentTextAlign: {
      /**
       * Set the text align attribute
       */
      setDocumentTextAlign: (alignment: string) => ReturnType;
      /**
       * Unset the text align attribute
       */
      unsetDocumentTextAlign: () => ReturnType;
    };
  }
}

export const DocumentNeueCoreEditor = ({
  content = '',
  editable = true,
  selected,
  selectionCoords,
  hasFocus,
  verticalAlignment,
  horizontalAlignment = 'center',
  rect,
  onUpdate,
  onCreate,
  onFocus,
  onBlur,
  onTextSize,
  onSetVerticalAlignment,
  onSetHorizontalAlignment,
}: {
  content?: any;
  editable: boolean;
  selected: boolean;
  selectionCoords: any;
  hasFocus: boolean;
  verticalAlignment: 'top' | 'center' | 'bottom';
  horizontalAlignment?: 'left' | 'center' | 'right';
  rect: { x: number; y: number; width: number; height: number };
  onUpdate?: (content: any) => void;
  onCreate?: (editor: any) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onTextSize: (width: number, height: number) => void;
  onSetVerticalAlignment: (verticalAlignment: 'top' | 'center' | 'bottom') => void;
  onSetHorizontalAlignment: (horizontalAlignment: 'left' | 'center' | 'right') => void;
}) => {
  const contentRef = useRef(content);
  const textRef = useRef<HTMLDivElement>();
  const [legacyTextRef, textRect] = useMeasure<HTMLDivElement>();
  const [size, setSize] = useState<Nullable<Size>>(null);

  const updateContentRef = (content: any) => {
    if (fastDeepEqual(contentRef.current, content)) return;
    contentRef.current = content;
  };

  const onEditorUpdate = ({ editor }: EditorEvents['update']) => {
    const json = editor.getJSON();
    const html = editor.getHTML();
    console.log('editor json', json);
    console.log('editor html', html);
    updateContentRef(html);
    onUpdate && onUpdate(html);
    // console.log('size', editor.view.dom.clientWidth, editor.view.dom.clientHeight);
    // updateTooltips();
    if (textRef.current) {
      let { width, height } = textRef.current.getBoundingClientRect();
      onTextSize && onTextSize(width + 2 * TEXT_BLOCK_PADDING, height + 2 * TEXT_BLOCK_PADDING);
      setSize({ width, height });
    }
    // console.log('size', ref?.getBoundingClientRect());
  };

  const { anchorRef, renderSpotlight } = useSpotlight({
    offset: 16,
    placement: 'top',
    strategy: 'fixed',
    styling: 'tooltip',
    theme: 'journey',
  });
  const activeMenuViewRef = useRef('');
  const [tooltipReference, setTooltipReference] = useState<HTMLElement>();
  const [tooltipContent, setTooltipContent] = useState('');

  const editor = useEditor({
    extensions: [
      Document,
      Text,
      Bold,
      Italic,
      BulletList,
      OrderedList,
      ListItem.extend({
        renderHTML({ HTMLAttributes }) {
          return ['li', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), ['div'], ['div', 0]];
        },
        addNodeView() {
          return ({ HTMLAttributes }) => {
            const listItem = document.createElement('li');
            const counterWrapper = document.createElement('div');
            const content = document.createElement('div');

            counterWrapper.contentEditable = 'false';

            Object.entries(this.options.HTMLAttributes).forEach(([key, value]) => {
              // @ts-ignore
              listItem.setAttribute(key, value);
            });
            listItem.append(counterWrapper, content);

            Object.entries(HTMLAttributes).forEach(([key, value]) => {
              listItem.setAttribute(key, value);
            });

            return {
              dom: listItem,
              contentDOM: content,
              update: (updatedNode) => {
                if (updatedNode.type !== this.type) {
                  return false;
                }
                return true;
              },
            };
          };
        },
      }),
      Placeholder.configure({
        includeChildren: true,
        showOnlyCurrent: true,
        placeholder: ({ node, editor }) => {
          if (editor.getText().length == 0) {
            return 'Add text';
          }
          return '';
        },
      }),
      Paragraph.configure({
        HTMLAttributes: {
          class: 'nodeview-paragraph',
        },
      }),
      Heading.configure({
        levels: [1, 2],
        HTMLAttributes: {
          class: 'nodeview-heading',
        },
      }),
      Link.extend({
        inclusive: false,
        addKeyboardShortcuts() {
          return {
            'Mod-k': () => {
              activeMenuViewRef.current = 'link';
              return this.editor.commands.setLink({
                href: this.editor.getAttributes('link').href,
              });
            },
          };
        },
      }).configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'neue-text-editor__link',
        },
      }),
      TaskList.configure({
        HTMLAttributes: {
          class: 'nodeview-task-list',
        },
      }),
      TaskItem.configure({
        nested: true,
      }),
      DraggableItem,
      TextEditorImage,
      Iframe,
      Dropcursor.configure({
        class: 'journey-text-editor__dropcursor',
      }),
      HorizontalRule,
      HardBreak,
      // TextAlign.configure({
      //   types: ['document'],
      //   // types: ['heading', 'paragraph'],
      //   defaultAlignment: 'center',
      // }),
      MutualActionPlanExtension,
      MutualActionPlanActionItemExtension,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Focus,
    ],
    editable: editable,
    onUpdate: debounce(onEditorUpdate, 500),
    onCreate: ({ editor }) => {
      onCreate && onCreate(editor);
    },
    onFocus: ({ editor }) => {
      onFocus && onFocus();
    },
    onBlur: ({ editor }) => {
      onBlur && onBlur();
    },
    content: content,
    editorProps: {
      attributes: {
        class: 'neue-contenteditable journey-text-editor__content flex flex-col gap-4',
      },
      // @ts-ignore
      handleDOMEvents: {
        mouseover: (view, event) => {
          const el = event.target as HTMLElement;
          console.log('mouseover', el.nodeName);
          if (el.nodeName === 'A') {
            console.log('mouseover a', el);
            setTooltipReference(el);
            anchorRef(el);
            setTooltipContent(el.getAttribute('href') || '');
            // tippyRef.current = tippy(el, {
            //   content: 'Tooltip',
            // });
            // tippyRef.current.show();
          } else {
            setTooltipReference(undefined);
            anchorRef(null);
          }
          return false;
        },
        mouseout: (view, event) => {
          const el = event.target as HTMLElement;
          console.log('mouseout', el, tooltipReference);
          if (el === tooltipReference) {
            setTooltipReference(undefined);
            anchorRef(null);
          }
          return false;
        },
      },
    },
  });

  useEffect(() => {
    if (!selected) {
      setTooltipReference(undefined);
      anchorRef(null);
    }
  }, [selected]);

  const editorRoot = useRef<HTMLDivElement>();

  const {
    x,
    y,
    reference,
    floating,
    strategy: calculatedStrategy,
    placement: calculatedPlacement,
    update,
    refs,
  } = useFloating({
    placement: 'top-start',
    strategy: 'absolute',
    middleware: [offset(8), flip(), shift({ padding: 16 })],
  });

  useEffect(() => {
    if (!refs.reference.current || !refs.floating.current) {
      return;
    }
    // Only call this when the floating element is rendered
    return autoUpdate(refs.reference.current, refs.floating.current, update);
  }, [refs.reference.current, refs.floating.current, update]);

  useEffect(() => {
    if (!editor) return;
    if (fastDeepEqual(contentRef.current, content)) return;
    console.log('content changed', contentRef.current, content);
    let { from, to } = editor.state.selection;
    editor.commands.setContent(content, false, {
      preserveWhitespace: 'full',
    });
    contentRef.current = content;
    if (editor.isFocused) {
      console.log('setting text selection', from, to);
      editor.commands.setTextSelection({ from, to });
    }
  }, [editor, content]);

  useEffect(() => {
    if (!editor) return;
    if (selected) {
      const pos = selectionCoords ? editor.view.posAtCoords({ left: selectionCoords.x, top: selectionCoords.y }) : null;
      editor.commands.focus(pos?.pos || 'end');
    } else {
      editor.commands.blur();
    }
  }, [editor, selected]);

  useEffect(() => {
    if (!editor) return;
    const ro = new ResizeObserver((entries) => {
      if (textRef.current) {
        const entry = entries[0];
        let { width, height } = entry.contentRect;
        // const { width, height } = textRef.current.getBoundingClientRect();
        console.log('textRect', width, height);
        onTextSize && onTextSize(width + 2 * TEXT_BLOCK_PADDING, height + 2 * TEXT_BLOCK_PADDING);
        setSize({ width, height });
      }
    });

    if (textRef.current) {
      ro.observe(textRef.current);
    }
    return () => {
      ro.disconnect();
    };
  }, [textRef, editor]);

  // useEffect(() => {
  //   if (textRect && textRef.current) {
  //     console.log('textRect', textRect);
  //     const { width, height } = textRef.current.getBoundingClientRect();
  //     console.log('textRect', width, height);
  //     onTextSize && onTextSize(width, height);
  //     setSize({ width, height });
  //   }
  // }, [textRect]);

  // const { from, to } = editor?.state.selection || {};

  // console.log('NeueCoreEditor', 'selected', selected, 'hasFocus', hasFocus, 'from', from, 'to', to);

  let translateY = 0;
  let rectHeight = rect.height - 2 * TEXT_BLOCK_PADDING;
  if (size && size.height < rectHeight) {
    if (verticalAlignment === 'top') {
      translateY = 0;
    } else if (verticalAlignment === 'center') {
      translateY = rectHeight / 2 - size.height / 2;
    } else if (verticalAlignment === 'bottom') {
      translateY = rectHeight - size.height;
    }
  }

  return (
    <>
      <div
        className={classNames('relative w-full h-full rounded-lg flex transition-opacity', {
          // 'items-start': verticalAlignment === 'top',
          // 'items-center': verticalAlignment === 'center',
          // 'items-end': verticalAlignment === 'bottom',
          'opacity-0': rect.height === 0,
          'opacity-100': rect.height > 0,
        })}
        ref={reference}
      >
        <div className='p-4 flex-1' onClick={(e) => editor && editor.view.dom.focus()}>
          <div
            className={classNames('w-full max-w-full h-auto')}
            style={{
              transform: `translateY(${translateY}px)`,
            }}
            ref={
              ((element: HTMLDivElement) => {
                textRef.current = element;
                legacyTextRef(element);
              }) as LegacyRef<HTMLDivElement>
            }
          >
            <EditorContent
              editor={editor}
              className={classNames('neue-text-editor w-full max-w-full h-auto cursor-text', {
                'text-left': horizontalAlignment === 'left',
                'text-center': horizontalAlignment === 'center',
                'text-right': horizontalAlignment === 'right',
              })}
              data-horizontal-alignment={horizontalAlignment}
            />
          </div>
        </div>
        {editable && (
          <NeueTextEditorBubbleMenuFixed
            ref={floating}
            x={x}
            y={y}
            activeMenuViewRef={activeMenuViewRef}
            strategy={calculatedStrategy}
            horizontalAlignment={horizontalAlignment}
            verticalAlignment={verticalAlignment}
            onSetVerticalAlignment={onSetVerticalAlignment}
            onSetHorizontalAlignment={onSetHorizontalAlignment}
            showMenu={editable && selected && x !== null && y !== null}
            editor={editor}
            hasFocus={hasFocus}
            selected={selected}
          />
        )}
        {renderSpotlight({
          content: tooltipContent,
          show: !!tooltipReference && !!tooltipContent,
        })}
        {!selected && editable && <div className='absolute inset-0'></div>}
      </div>
    </>
  );
};
